import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import api from '../services/api';
import { getUserData } from '../utils/cookies';
import {
  PageName,
  ApiResponse,
  PlateDatasetStatistic,
  PlateRegionStatistic,
  User,
} from '../types';
import { DrawerNavigation, PlateIcon, DRAWER_WIDTH, COLLAPSED_WIDTH } from '../components';
import { getTaskBgColor } from '../constants/taskConfig';

// Constants
const THEME_COLOR = '#ff9800';
const BAR_HEIGHT = 28;
const CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 5 };

// Helper to format large numbers
const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return String(value);
};

// Country flag emoji based on region code
const getCountryFlag = (code: string): string => {
  if (code.startsWith('us-')) return '🇺🇸';
  if (code.startsWith('ca-')) return '🇨🇦';
  return '';
};

interface PlateStatisticsProps {
  onLogout: () => void;
  onNavigate: (page: PageName) => void;
}

function PlateStatistics({ onLogout, onNavigate }: PlateStatisticsProps) {
  const theme = useTheme();
  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User data
  const [user, setUser] = useState<User | null>(null);

  // Statistics data
  const [datasets, setDatasets] = useState<PlateDatasetStatistic[]>([]);
  const [regions, setRegions] = useState<PlateRegionStatistic[]>([]);

  // Scale type for charts (linear or log)
  const [datasetsScale, setDatasetsScale] = useState<'linear' | 'log'>('linear');
  const [regionsScale, setRegionsScale] = useState<'linear' | 'log'>('linear');

  // Ref to prevent double fetching in StrictMode
  const hasFetched = useRef(false);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Fetch statistics data
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get user from cookies (stored during login) - no API call needed
        const storedUser = getUserData();
        if (storedUser) {
          setUser({
            id: storedUser.id,
            username: storedUser.username,
            roles: storedUser.roles,
          });
        }

        const [datasetsRes, regionsRes] = await Promise.all([
          api.get<ApiResponse<PlateDatasetStatistic[]>>('statistics/plate/datasets'),
          api.get<ApiResponse<PlateRegionStatistic[]>>('statistics/plate/regions'),
        ]);

        setDatasets(datasetsRes.data.results || []);
        setRegions(regionsRes.data.results || []);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
        setError('Failed to load statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Sort data by count descending
  const sortedDatasets = [...datasets].sort((a, b) => b.count - a.count);
  const sortedRegions = [...regions].sort((a, b) => b.count - a.count);

  // Filter out zero counts for log scale (log(0) = NaN)
  const chartDatasets = datasetsScale === 'log' ? sortedDatasets.filter((d) => d.count > 0) : sortedDatasets;
  const chartRegions = regionsScale === 'log' ? sortedRegions.filter((r) => r.count > 0) : sortedRegions;

  // Calculate totals
  const totalPlates = datasets.reduce((sum, d) => sum + d.count, 0);

  // Calculate chart heights
  const datasetsChartHeight = Math.max(300, chartDatasets.length * BAR_HEIGHT + 60);
  const regionsChartHeight = Math.max(400, chartRegions.length * BAR_HEIGHT + 60);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: THEME_COLOR,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <PlateIcon size={28} />
            <Typography variant="h6" noWrap>
              Plate Statistics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <DrawerNavigation
        currentPage="statsPlate"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Summary Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: getTaskBgColor('plate', theme.palette.mode),
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PlateIcon size={40} />
              </Box>
              <Box>
                <Typography variant="h5">Plate Statistics</Typography>
                <Typography variant="body2" color="text.secondary">
                  Overview of license plate annotations
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: getTaskBgColor('plate', theme.palette.mode) }}>
                  <Typography variant="h4" color="primary">
                    {formatNumber(totalPlates)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Plates
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{datasets.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Datasets
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{regions.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Regions
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">
                    {regions.filter((r) => r.code.startsWith('us-')).length} / {regions.filter((r) => r.code.startsWith('ca-')).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    US / Canada States
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Charts */}
          <Grid container spacing={3}>
            {/* Datasets Chart */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    Plates by Dataset ({chartDatasets.length})
                  </Typography>
                  <ToggleButtonGroup
                    value={datasetsScale}
                    exclusive
                    onChange={(_, value) => value && setDatasetsScale(value)}
                    size="small"
                  >
                    <ToggleButton value="linear">Linear</ToggleButton>
                    <ToggleButton value="log">Log</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ height: datasetsChartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartDatasets}
                      layout="vertical"
                      margin={CHART_MARGIN}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tickFormatter={formatNumber}
                        scale={datasetsScale}
                        domain={datasetsScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={150}
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <Tooltip
                        formatter={(value) => [Number(value).toLocaleString(), 'Count']}
                      />
                      <Bar dataKey="count" fill={THEME_COLOR} radius={[0, 4, 4, 0]}>
                        {chartDatasets.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index % 2 === 0 ? THEME_COLOR : '#ffb74d'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Regions Chart */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    Plates by Region ({chartRegions.length})
                  </Typography>
                  <ToggleButtonGroup
                    value={regionsScale}
                    exclusive
                    onChange={(_, value) => value && setRegionsScale(value)}
                    size="small"
                  >
                    <ToggleButton value="linear">Linear</ToggleButton>
                    <ToggleButton value="log">Log</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ height: regionsChartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartRegions.map((r) => ({
                        ...r,
                        displayName: `${getCountryFlag(r.code)} ${r.label}`,
                      }))}
                      layout="vertical"
                      margin={CHART_MARGIN}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tickFormatter={formatNumber}
                        scale={regionsScale}
                        domain={regionsScale === 'log' ? [1, 'auto'] : [0, 'auto']}
                      />
                      <YAxis
                        type="category"
                        dataKey="displayName"
                        width={180}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <Tooltip
                        formatter={(value) => [Number(value).toLocaleString(), 'Count']}
                        labelFormatter={(label) => label}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {chartRegions.map((region, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={region.code.startsWith('us-') ? '#1976d2' : '#d32f2f'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#1976d2', borderRadius: 1 }} />
                    <Typography variant="caption">USA</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#d32f2f', borderRadius: 1 }} />
                    <Typography variant="caption">Canada</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default PlateStatistics;
